import { SPEC_PARAMETER_KIND } from "state_management/lims/limsSpecs"

// specVersionDiff (SPEC-055 R33/R34 · #4098) — what changed between two QC-spec versions.
//
// The version history already showed WHICH versions exist and who signed them, but never WHAT was
// changed, so an auditor could see that v2 supersedes v1 without being able to say how they differ.
// Every version row carries its own frozen `parameters` / `formula` snapshot, so the comparison is
// pure client-side arithmetic — no new endpoint, and deliberately so: a backend diff route would
// fetch data the frontend already holds.
//
// SPEC-063 (#4149): the diff NO LONGER covers the consumable requirements. They were split out of
// the versioned specification, so versions created since the split carry no requirement snapshot at
// all — comparing them would report every requirement as deleted on every new version, which is the
// opposite of what happened. The requirements that older versions froze are left untouched (they are
// part of a signed record); they are simply not what these versions are about any more.
//
// Field identifiers returned here are EXISTING i18n keys (the same ones SpecEditor and
// RequirementsSection label their inputs with), so the rendered diff names a field exactly as the
// editor does and no new vocabulary is invented for it.

// Parameter fields worth reporting, in editor order. `key` is the i18n key for the label.
const PARAM_FIELDS = [
  { path: "name", key: "limsSpecParameterName" },
  { path: "kind", key: "limsSpecKind" },
  { path: "unit", key: "limsSpecUnit" },
  { path: "significantDigits", key: "limsSpecSignificantDigits" },
  { path: "criterionType", key: "limsSpecCriterionType" },
  { path: "min", key: "limsSpecMin" },
  { path: "max", key: "limsSpecMax" },
  { path: "formula", key: "limsCalcFormula" },
  { path: "individualTolerancePercent", key: "limsCalcIndividualTolerance" },
]

const at = (obj, path) => path.split(".").reduce((o, k) => (o === null || o === undefined ? o : o[k]), obj)

// Blank-insensitive comparison: null, undefined and "" all mean "unset", so clearing a field that
// was already empty is not a change. Numbers are compared by value, not by 5 vs "5".
const isBlank = (v) => v === null || v === undefined || v === ""
const same = (a, b) => {
  if (isBlank(a) && isBlank(b)) return true
  if (isBlank(a) !== isBlank(b)) return false
  if (typeof a === "number" || typeof b === "number") return Number(a) === Number(b)
  return String(a) === String(b)
}

// A calculated parameter's inputs are a list; compare them as their ordered key list, which is what
// the formula actually references. A reorder or rename shows up, a cosmetic label edit does not.
const inputKeys = (p) => ((Array.isArray(p.inputs) ? p.inputs : []).map((i) => i.key).join(", "))

const paramLabel = (p, fallback) => (p && p.name) || fallback

// Match rows across versions by identity, NOT by index: inserting a parameter at the top would
// otherwise read as "every parameter changed". Identity is the row's `id` when it has one (the
// editor preserves parameter ids across saves), else its natural key. Duplicate keys within one
// version get an occurrence suffix, so two requirements of the same type and name stay distinct
// instead of collapsing into one match.
function keyList(list, natural) {
  const seen = new Map()
  return list.map((item, i) => {
    const base = (item && item.id) || natural(item, i)
    const n = (seen.get(base) || 0) + 1
    seen.set(base, n)
    return n === 1 ? base : `${base}#${n}`
  })
}

function pair(prevList, nextList, natural) {
  const prev = Array.isArray(prevList) ? prevList : []
  const next = Array.isArray(nextList) ? nextList : []
  const prevKeys = keyList(prev, natural)
  const nextKeys = keyList(next, natural)
  const prevByKey = new Map(prevKeys.map((k, i) => [k, prev[i]]))
  const nextKeySet = new Set(nextKeys)

  const matched = []
  const added = []
  next.forEach((item, i) => {
    const k = nextKeys[i]
    if (prevByKey.has(k)) matched.push([prevByKey.get(k), item])
    else added.push(item)
  })
  const removed = prev.filter((_item, i) => !nextKeySet.has(prevKeys[i]))
  return { matched, added, removed }
}

function fieldChanges(before, after, fields) {
  const out = []
  for (const f of fields) {
    const a = at(before, f.path)
    const b = at(after, f.path)
    if (!same(a, b)) out.push({ labelKey: f.key, from: a, to: b })
  }
  return out
}

// Diff one version against the one before it. `prev` is null for the first version.
// Returns { isInitial, hasChanges, formula, parameters:{added,removed,changed} }
export function diffSpecVersions(prev, next) {
  if (!next) return { isInitial: false, hasChanges: false, parameters: null, formula: null }
  if (!prev) {
    return {
      isInitial: true,
      hasChanges: false,
      formula: null,
      parameters: { added: [], removed: [], changed: [] },
    }
  }

  const keyOfParam = (p, i) => (p && p.name) || `#${i}`
  const p = pair(prev.parameters, next.parameters, keyOfParam)
  const paramChanged = []
  for (const [before, after] of p.matched) {
    const fields = fieldChanges(before, after, PARAM_FIELDS)
    // The calculated-input list is a field too, just not a scalar one.
    if (inputKeys(before) !== inputKeys(after)) {
      fields.push({ labelKey: "limsCalcInputs", from: inputKeys(before), to: inputKeys(after) })
    }
    const aggBefore = before.aggregate && before.aggregate.inputKey
    const aggAfter = after.aggregate && after.aggregate.inputKey
    if (!same(aggBefore, aggAfter)) {
      fields.push({ labelKey: "limsCalcAggregate", from: aggBefore, to: aggAfter })
    }
    if (fields.length) paramChanged.push({ label: paramLabel(after, paramLabel(before, "—")), fields })
  }

  const formula = same(prev.formula, next.formula) ? null : { from: prev.formula, to: next.formula }

  const parameters = {
    added: p.added.map((x, i) => paramLabel(x, `#${i + 1}`)),
    removed: p.removed.map((x, i) => paramLabel(x, `#${i + 1}`)),
    changed: paramChanged,
  }
  const hasChanges = Boolean(
    formula || parameters.added.length || parameters.removed.length || parameters.changed.length
  )
  return { isInitial: false, hasChanges, formula, parameters }
}

// How a value reads in the diff. A blank becomes an em dash so "set" and "cleared" are legible, and
// a parameter KIND is an enum the editor labels, not a raw token.
export function diffValueLabel(value, t) {
  if (isBlank(value)) return "—"
  if (value === SPEC_PARAMETER_KIND.CALCULATED) return t("limsSpecKindCalculated")
  if (value === SPEC_PARAMETER_KIND.MEASURED) return t("limsSpecKindMeasured")
  return String(value)
}
